import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, ArrowLeft, CheckCircle, XCircle, Eye, Download, FileText, 
  DollarSign, Wrench, Users, Calendar, AlertCircle, Search,
  Filter, Building2, TrendingUp, Home, RefreshCw, X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { financialAPI } from '../services/api';
import api from '../services/api';
import { OccupancyChart, PaymentDistributionChart, RevenueChart, TrafficChart } from '../components/ChartWidgets';

interface PendingItem {
  id: string;
  type: 'expense' | 'project';
  title: string;
  amount: number;
  date: string;
  status: string;
  requestor: string;
  description: string;
  category: string;
  urgency: string;
  hasInvoice?: boolean;
  hasQuotes?: boolean;
  quotes?: { provider: string; amount: number; file: string }[];
}

interface MorosoItem {
  id: string;
  unit: string;
  name: string;
  debt: number;
  months: number;
  lastPayment: string;
  phone: string;
  email: string;
}

interface ReportItem {
  id: string;
  name: string;
  type: string;
  date: string;
  size: string;
}

export default function DirectiveUserDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('approvals');
  const [selectedItem, setSelectedItem] = useState<PendingItem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [morosos, setMorosos] = useState<MorosoItem[]>([]);
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      if (!condoId) {
        setPendingItems([]);
        setMorosos([]);
        setReports([]);
        setLoading(false);
        return;
      }

      const [morososRes, reportsRes] = await Promise.all([
        financialAPI.getMorosos(condoId).catch(() => null),
        financialAPI.getReports(condoId).catch(() => null),
      ]);
      const analyticsRes = await api.get(`/condos/${condoId}/analytics`).catch(() => null);
      const analyticsData = analyticsRes?.data?.data || analyticsRes?.data || null;
      setAnalytics(analyticsData);

      // Morosos
      const morososData = morososRes?.data?.data || morososRes?.data || [];
      setMorosos(Array.isArray(morososData) ? morososData.map((m: any) => ({
        id: String(m.id || ''),
        unit: m.unit || m.unit_number || '—',
        name: m.name || m.resident_name || '—',
        debt: Number(m.debt || m.total_debt || m.amount || 0),
        months: Number(m.months_overdue || m.months || 0),
        lastPayment: m.last_payment_date || m.lastPayment || '—',
        phone: m.phone || '—',
        email: m.email || '—',
      })) : []);

      // Reports
      const reportsData = reportsRes?.data?.data || reportsRes?.data || [];
      setReports(Array.isArray(reportsData) ? reportsData.map((r: any) => ({
        id: String(r.id || ''),
        name: r.name || r.title || 'Reporte',
        type: r.type || 'monthly',
        date: r.date || r.created_at || '—',
        size: r.size || '—',
      })) : []);

      // Pending items: try expenses with pending status
      // The expenses API may have pending approvals; if not, show empty
      setPendingItems([]);
    } catch (err) {
      console.error('Error loading directive dashboard data:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleApprove = (id: string) => {
    alert(`✅ Gasto/Proyecto ${id} aprobado correctamente`);
  };

  const handleReject = (id: string) => {
    alert(`❌ Gasto/Proyecto ${id} rechazado`);
  };

  const openDetail = (item: PendingItem) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const pendingCount = pendingItems.filter(p => p.status === 'pending').length;
  const pendingAmount = pendingItems
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <div className="min-h-full bg-[var(--v-base-bg)] flex items-center justify-center" style={{ fontFamily: 'Inter, sans-serif' }}>
        <p className="text-gray-500 dark:text-[var(--v-text-secondary)]">Cargando panel directivo...</p>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-white dark:bg-[var(--v-surface-1)] border-b border-gray-200 dark:border-[var(--v-surface-3)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] dark:text-[var(--v-text-primary)] hover:bg-gray-100 dark:hover:bg-[var(--v-surface-2)] rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#36CCD8] rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">Panel Directivo</span>
                <span className="text-xs text-[#36CCD8] block -mt-1">Usuario Directivo</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <p className="text-sm font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">{user?.name || 'Directivo'}</p>
              <p className="text-xs text-gray-500 dark:text-[var(--v-text-secondary)]">Control Financiero</p>
            </div>
          </div>
        </div>
      </header>

      <main className="p-6">
        <div className="bg-[#36CCD8] rounded-2xl p-6 text-white mb-6 shadow-lg shadow-[#36CCD8]/20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <p className="text-white/80 text-sm font-medium mb-1">Bienvenido, Directivo</p>
              <h1 className="text-2xl font-bold">Control de Gastos y Proyectos</h1>
              <p className="text-white/90 mt-1">Revisa y aprueba los gastos del condominio</p>
            </div>
            
            <div className="flex gap-3">
              <div className="px-4 py-2 bg-white/20 rounded-xl backdrop-blur">
                <p className="text-xs text-white/80">Pendientes</p>
                <p className="text-2xl font-bold">{pendingCount}</p>
              </div>
              <div className="px-4 py-2 bg-white/20 rounded-xl backdrop-blur">
                <p className="text-xs text-white/80">Por Aprobar</p>
                <p className="text-2xl font-bold">${pendingAmount.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'approvals', label: 'Pendientes de Aprobación', icon: FileText },
            { id: 'morosos', label: 'Morosos', icon: AlertCircle },
            { id: 'reports', label: 'Centro de Reportes', icon: TrendingUp },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-[#36CCD8] text-white shadow-lg'
                  : 'bg-white dark:bg-[var(--v-surface-1)] text-[#2D3748] dark:text-[var(--v-text-primary)] hover:bg-gray-100 dark:hover:bg-[var(--v-surface-2)] border border-gray-200 dark:border-[var(--v-surface-3)]'
              }`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6 text-center">
            <p className="text-red-600 mb-3">Error al cargar los datos.</p>
            <button onClick={loadData} className="px-6 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] inline-flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Reintentar
            </button>
          </div>
        )}

        {analytics && (
          <section className="mb-6" aria-labelledby="directive-analytics-heading">
            <h2 id="directive-analytics-heading" className="text-xl font-bold text-[#2D3748] dark:text-[var(--v-text-primary)] mb-4">Analíticas del condominio</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RevenueChart data={[{ month: 'Anterior', revenue: Number(analytics.monthlyComparison?.income?.lastMonth || 0), expenses: Number(analytics.monthlyComparison?.expenses?.lastMonth || 0) }, { month: 'Actual', revenue: Number(analytics.monthlyComparison?.income?.thisMonth || 0), expenses: Number(analytics.monthlyComparison?.expenses?.thisMonth || 0) }]} />
              <TrafficChart data={(analytics.visitorTraffic || []).map((item: any) => ({ day: String(item.date || ''), visitors: Number(item.count || 0) }))} />
              <PaymentDistributionChart data={(analytics.paymentDistribution?.byType || []).map((item: any) => ({ name: String(item.type || 'Sin tipo'), value: Number(item.total || 0) }))} />
              <OccupancyChart data={[{ unit: 'Unidades', occupied: Number(analytics.occupancy?.occupiedUnits || 0), available: Math.max(0, Number(analytics.occupancy?.totalUnits || 0) - Number(analytics.occupancy?.occupiedUnits || 0)) }]} />
            </div>
          </section>
        )}

        {activeTab === 'approvals' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl border border-gray-100 dark:border-[var(--v-surface-3)] shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-[var(--v-surface-3)] flex items-center justify-between">
                <h3 className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">Gastos y Proyectos Pendientes</h3>
                <div className="flex gap-2">
                  <button className="p-2 text-gray-400 dark:text-[var(--v-text-secondary)] hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl">
                    <Search className="w-5 h-5" />
                  </button>
                  <button className="p-2 text-gray-400 dark:text-[var(--v-text-secondary)] hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl">
                    <Filter className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {pendingItems.filter(p => p.status === 'pending').length === 0 ? (
                <div className="p-12 text-center text-gray-400 dark:text-[var(--v-text-secondary)]">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No hay gastos pendientes de aprobación</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {pendingItems.filter(p => p.status === 'pending').map((item) => (
                    <div key={item.id} className="p-6 hover:bg-gray-50 dark:hover:bg-[var(--v-surface-2)] transition-colors">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        <div className="w-12 h-12 bg-[#36CCD8]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          {item.type === 'expense' ? <DollarSign className="w-6 h-6 text-[#36CCD8]" /> : <Wrench className="w-6 h-6 text-[#36CCD8]" />}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">{item.title}</h4>
                            <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                              item.urgency === 'high' ? 'bg-red-100 text-red-700' : 
                              item.urgency === 'medium' ? 'bg-amber-100 text-amber-700' : 
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {item.urgency === 'high' ? 'Urgente' : item.urgency === 'medium' ? 'Media' : 'Baja'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-[var(--v-text-secondary)] mb-2">{item.description}</p>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 dark:text-[var(--v-text-secondary)]">
                            <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {item.requestor}</span>
                            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {item.date}</span>
                            <span className="flex items-center gap-1"><Building2 className="w-4 h-4" /> {item.category}</span>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#36CCD8]">${item.amount.toLocaleString()}</p>
                          <p className="text-xs text-gray-400 dark:text-[var(--v-text-secondary)]">{item.type === 'expense' ? 'Gasto' : 'Proyecto'}</p>
                        </div>
                        
                        <div className="flex gap-2">
                          <button onClick={() => openDetail(item)} className="px-4 py-2 bg-gray-100 dark:bg-[var(--v-surface-2)] text-[#2D3748] dark:text-[var(--v-text-primary)] font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-[var(--v-surface-3)] flex items-center gap-2">
                            <Eye className="w-4 h-4" /> Ver
                          </button>
                          <button onClick={() => handleApprove(item.id)} className="px-4 py-2 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" /> Aprobar
                          </button>
                          <button onClick={() => handleReject(item.id)} className="px-4 py-2 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 flex items-center gap-2">
                            <XCircle className="w-4 h-4" /> Rechazar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'morosos' && (
          <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl border border-gray-100 dark:border-[var(--v-surface-3)] shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-[var(--v-surface-3)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <h3 className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">Lista de Morosos</h3>
              </div>
              {morosos.length > 0 && (
                <button className="px-4 py-2 bg-[#36CCD8]/10 text-[#36CCD8] font-bold rounded-xl text-sm">
                  Enviar Recordatorios
                </button>
              )}
            </div>
            
            {morosos.length === 0 ? (
              <div className="p-12 text-center text-gray-400 dark:text-[var(--v-text-secondary)]">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay morosos registrados</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {morosos.map((m) => (
                  <div key={m.id} className="p-6 hover:bg-gray-50 dark:hover:bg-[var(--v-surface-2)] transition-colors">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                          <Users className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                          <p className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">{m.name}</p>
                          <p className="text-sm text-gray-500 dark:text-[var(--v-text-secondary)]">Unidad {m.unit}</p>
                          <p className="text-xs text-gray-400 dark:text-[var(--v-text-secondary)]">{m.email} • {m.phone}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-2xl font-bold text-red-500">${m.debt.toLocaleString()}</p>
                        <p className="text-sm text-gray-500 dark:text-[var(--v-text-secondary)]">{m.months} meses de adeudo</p>
                        <p className="text-xs text-gray-400 dark:text-[var(--v-text-secondary)]">Último pago: {m.lastPayment}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl border border-gray-100 dark:border-[var(--v-surface-3)] shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-[var(--v-surface-3)]">
              <h3 className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">Centro de Reportes Financieros</h3>
            </div>
            
            {reports.length === 0 ? (
              <div className="p-12 text-center text-gray-400 dark:text-[var(--v-text-secondary)]">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay reportes disponibles</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {reports.map((report) => (
                  <div key={report.id} className="p-6 hover:bg-gray-50 dark:hover:bg-[var(--v-surface-2)] transition-colors flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#36CCD8]/10 rounded-xl flex items-center justify-center">
                        <FileText className="w-6 h-6 text-[#36CCD8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">{report.name}</p>
                        <p className="text-sm text-gray-500 dark:text-[var(--v-text-secondary)]">{report.date} • {report.size}</p>
                      </div>
                    </div>
                    
                    <button className="px-4 py-2 bg-[#36CCD8]/10 text-[#36CCD8] font-bold rounded-xl hover:bg-[#36CCD8]/20 flex items-center gap-2">
                      <Download className="w-4 h-4" /> Descargar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {showDetailModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 dark:border-[var(--v-surface-3)] flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">Detalle de {selectedItem.type === 'expense' ? 'Gasto' : 'Proyecto'}</h3>
              <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-[var(--v-surface-2)] rounded-xl">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)] text-lg mb-2">{selectedItem.title}</h4>
                <p className="text-gray-600 dark:text-[var(--v-text-secondary)]">{selectedItem.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-[var(--v-surface-2)] rounded-xl p-4">
                  <p className="text-sm text-gray-500 dark:text-[var(--v-text-secondary)] mb-1">Monto</p>
                  <p className="text-2xl font-bold text-[#36CCD8]">${selectedItem.amount.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 dark:bg-[var(--v-surface-2)] rounded-xl p-4">
                  <p className="text-sm text-gray-500 dark:text-[var(--v-text-secondary)] mb-1">Solicitado por</p>
                  <p className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">{selectedItem.requestor}</p>
                </div>
              </div>
              
              {selectedItem.hasQuotes && selectedItem.quotes && (
                <div>
                  <h5 className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)] mb-3">Cotizaciones (3 requeridas)</h5>
                  <div className="space-y-2">
                    {selectedItem.quotes.map((quote, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[var(--v-surface-2)] rounded-xl">
                        <span className="font-medium">{quote.provider}</span>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">${quote.amount.toLocaleString()}</span>
                          <button className="text-[#36CCD8] hover:underline text-sm">Ver PDF</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedItem.hasInvoice && (
                <div>
                  <h5 className="font-bold text-[#2D3748] dark:text-[var(--v-text-primary)] mb-3">Factura / Comprobante</h5>
                  <div className="p-4 bg-gray-50 dark:bg-[var(--v-surface-2)] rounded-xl flex items-center justify-between">
                    <span className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#36CCD8]" /> Factura_{selectedItem.id}.pdf</span>
                    <button className="text-[#36CCD8] hover:underline">Ver documento</button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-100 dark:border-[var(--v-surface-3)] flex gap-3">
              <button onClick={() => { handleApprove(selectedItem.id); setShowDetailModal(false); }} className="flex-1 py-3 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600">
                Aprobar
              </button>
              <button onClick={() => { handleReject(selectedItem.id); setShowDetailModal(false); }} className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600">
                Rechazar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
